import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { HeroBlock } from "@/components/HeroBlock.tsx";
import { Separator } from "@/components/Separator.tsx";
import { CreateFormAction } from "@/components/forms/CreateFormAction.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { IconRadio, type IconRadioContentProps } from "@/components/forms/IconRadio.tsx";
import { MonthlyCostElement, MonthlyCostField } from "@/components/forms/MonthlyCostField.tsx";
import RA from "@/components/forms/Radio.module.css";
import R from "@/components/forms/Radiogroup.module.css";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { LoadingSuspense } from "@/components/loading/Loading.tsx";
import { useConfig } from "@/config.ts";
import { FF_customSubnetEnabledAtom } from "@/featureFlags.ts";
import {
    getResourceDescription,
    getResourceTypeName,
    resourceTypesEnabledAtom,
    type ResourceType,
} from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation, useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { BillingAccountField } from "@/modules/billing/forms/BillingAccountField.tsx";
import { LocationField, type LocationInputs } from "@/modules/location/LocationField.tsx";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { AssignToSelect } from "@/modules/network/AssignToSelect.tsx";
import { CreateAndAssignIpField, IpAddressAllPrices } from "@/modules/network/CreateAndAssignIpField.tsx";
import { VpcNetworkSelect } from "@/modules/network/VpcNetworkSelect.tsx";
import { AssignedToList } from "@/modules/network/firewall/AssignedToList.tsx";
import { RulesList } from "@/modules/network/firewall/RulesList.tsx";
import { addResourceToFirewallMutation, createFirewallMutation } from "@/modules/network/firewall/firewallApi.ts";
import { firewallInboundDescription, firewallOutboundDescription } from "@/modules/network/firewall/firewallLabels.tsx";
import { ruleTypes, type RuleType } from "@/modules/network/firewall/ruleSpec.tsx";
import { createFullIpAddressMutation } from "@/modules/network/ipAddress/ipAddressApi.ts";
import {
    getAssignBodyFromResource,
    UNASSIGNED_RESOURCE,
    type IpAssignableResource,
} from "@/modules/network/ipAddress/resourceId.ts";
import { firewallLink, ipAddressLink, loadBalancerLink, vpcLink } from "@/modules/network/links.ts";
import { ForwardingRulesList } from "@/modules/network/loadbalancer/ForwardingRulesList.tsx";
import { TargetServersList } from "@/modules/network/loadbalancer/TargetServersList.tsx";
import { createLoadBalancerMutation } from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import { getDefaultVpc } from "@/modules/network/networkLogic.ts";
import { createVpcNetworkMutation, getSingleQuery } from "@/modules/network/vpc/vpcApi.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { FREE_PRICE, getIpAddressPrices, getLoadBalancerPrice } from "@/modules/pricing/resourcePricing.ts";
import { cn } from "@/utils/classNames.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useControlledState } from "@react-stately/utils";
import { Navigate } from "@tanstack/react-router";
import type {
    FirewallAssignedResource,
    FirewallCreateBody,
    FirewallRule,
    FirewallRuleEndpointSpecType,
    LoadBalancerCreateBody,
} from "@warrenio/api-spec/spec.oats.gen";
import { findById } from "@warrenio/utils/collections/findById";
import { notNull } from "@warrenio/utils/notNull";
import type { Subset } from "@warrenio/utils/types/Subset";
import { useAtomValue } from "jotai/react";
import { atom } from "jotai/vanilla";
import { Suspense, useState, type ReactNode } from "react";
import { Input, Radio, RadioGroup } from "react-aria-components";
import { Controller, useForm, useFormContext, type SubmitHandler } from "react-hook-form";
import { isArray, omit } from "remeda";

interface ItemType extends IconRadioContentProps {
    id: NetworkFormType;
    description: ReactNode;
}

const typesAtom = atom((get) => {
    const items: ItemType[] = [
        {
            id: "ip_address",
            title: getResourceTypeName("ip_address"),
            icon: "jp-network-2-icon",
            description: (
                <Trans>
                    Allocating a new IPv4 address allows you to assign it to one of your existing resources or reserve
                    it for later use.
                </Trans>
            ),
        },
        {
            id: "vpc",
            title: getResourceTypeName("vpc"),
            icon: "jp-network-icon",
            description: (
                <Trans>
                    A virtual private cloud (VPC) is a private network interface for collections of your resources. VPC
                    network is available at no additional cost.
                </Trans>
            ),
        },
        {
            id: "load_balancer",
            title: getResourceTypeName("load_balancer"),
            icon: "jp-load-balancer-icon",
            description: getResourceDescription("load_balancer"),
        },
        {
            id: "firewall",
            title: getResourceTypeName("firewall"),
            icon: "i-lucide:shield-ban",
            description: getResourceDescription("firewall"),
        },
    ];
    const enabled = get(resourceTypesEnabledAtom);
    return items.filter((item) => enabled[item.id]);
});

export interface CommonInputs extends LocationInputs {
    name: string;
    billing_account_id?: number;
}

export type NetworkFormType = Subset<"ip_address" | "vpc" | "load_balancer" | "firewall", ResourceType>;

type Inputs = AddressCreateInputs | VpcCreateInputs | BalancerCreateInputs | FirewallCreateInputs;

export interface NetworkCreateProps {
    typeId?: NetworkFormType;
    defaultTypeId?: NetworkFormType;
    onChangeType?: (type: NetworkFormType) => void;

    inputs?: Partial<Inputs>;
}

function NetworkForm({ inputs, typeId: controlledTypeId, defaultTypeId, onChangeType }: NetworkCreateProps) {
    const { t } = useLingui();
    const types = useAtomValue(typesAtom);
    const [typeId, setType] = useControlledState(controlledTypeId, defaultTypeId ?? types[0].id, onChangeType);

    const type = findById(types, typeId);

    const commonProps = {
        wide: types.length > 3 ? true : undefined,
    };

    return (
        <>
            <WRadioGroup
                label={t`Type`}
                isRequired
                value={typeId}
                onChange={setType}
                description={type.description}
                {...commonProps}
            >
                {types.map((item) => (
                    <IconRadio key={item.id} {...item} />
                ))}
            </WRadioGroup>
            <Separator />
            <LoadingSuspense>
                {typeId === "vpc" ? (
                    <VpcCreateForm inputs={inputs as Partial<VpcCreateInputs>} />
                ) : typeId === "load_balancer" ? (
                    <BalancerCreateForm inputs={inputs as Partial<BalancerCreateInputs>} />
                ) : typeId === "firewall" ? (
                    <FirewallCreateForm inputs={inputs as Partial<FirewallCreateInputs>} />
                ) : (
                    <AddressCreateForm inputs={inputs as Partial<AddressCreateInputs>} />
                )}
            </LoadingSuspense>
        </>
    );
}

interface AddressCreateInputs extends CommonInputs {
    assignedResource: IpAssignableResource | UNASSIGNED_RESOURCE;
}

function AddressCreateForm({ inputs }: { inputs?: Partial<AddressCreateInputs> }) {
    const { t } = useLingui();
    const ipMutation = useStandardMutation(createFullIpAddressMutation);
    const locations = useLocationsForType("ip_address");
    const form = useForm<AddressCreateInputs>({
        disabled: ipMutation.isPending,
        defaultValues: {
            location: locations.defaultLocation,
            assignedResource: UNASSIGNED_RESOURCE,
            ...inputs,
        },
    });
    const { watch, control } = form;

    const onSubmit: SubmitHandler<AddressCreateInputs> = async ({ location, assignedResource, ...body }) => {
        await ipMutation.mutateAsync({
            location,
            assignBody:
                assignedResource !== UNASSIGNED_RESOURCE ? getAssignBodyFromResource(assignedResource) : undefined,
            body,
        });
    };

    if (ipMutation.isSuccess) {
        return <Navigate {...ipAddressLink(ipMutation.data.result)} />;
    }

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            {locations.showLocations && (
                <>
                    <LocationField locations={locations} />
                    <Separator />
                </>
            )}

            <FormField
                label={t`Assign to`}
                description={t`Choose a resource to assign the IP to or leave it unassigned. You can always assign the IP to a suitable resource later`}
            >
                <Controller
                    control={control}
                    name="assignedResource"
                    render={({ field }) => (
                        <AssignToSelect
                            location={watch("location")}
                            isDisabled={field.disabled}
                            defaultValue={field.value ?? UNASSIGNED_RESOURCE}
                            onChange={field.onChange}
                        />
                    )}
                />
            </FormField>

            <NameField isRequired={false} />

            <Separator />

            <Suspense>
                <AddressMonthlyCostField
                    location={watch("location")}
                    billing_account_id={watch("billing_account_id")}
                />
            </Suspense>

            <BillingAccountField resourceType="ip_address" isRequired />

            <Separator />

            <CreateFormAction resourceType="ip_address" />
        </WHookForm>
    );
}

export function AddressMonthlyCostField({
    location,
    billing_account_id,
}: {
    location: string;
    billing_account_id?: number;
}) {
    const priceList = useSuspenseQueryAtom(pricesAtom);

    const prices = getIpAddressPrices(priceList, location);
    return (
        <MonthlyCostField defaultLocation={location} billingAccountId={billing_account_id}>
            <IpAddressAllPrices prices={prices} billingAccountId={billing_account_id} />
        </MonthlyCostField>
    );
}

export interface BalancerCreateInputs
    extends Omit<LoadBalancerCreateBody, "network_uuid">, Pick<CommonInputs, "location"> {
    network_uuid: string | null;
}

function BalancerCreateForm({ inputs }: { inputs?: Partial<BalancerCreateInputs> }) {
    const { t } = useLingui();
    const { privateNetworksEnabled } = useConfig();
    const createMutation = useStandardMutation(createLoadBalancerMutation);
    const locations = useLocationsForType("load_balancer");
    // XXX: Might be a better way to get the default vpc for vpc select
    const { data } = useStandardSuspenseQuery(getSingleQuery, { location: locations.defaultLocation });
    const networkUuid = getDefaultVpc([...data.values()])?.uuid;
    const form = useForm<BalancerCreateInputs>({
        disabled: createMutation.isPending,
        defaultValues: {
            reserve_public_ip: true,
            location: locations.defaultLocation,
            // NB: react-hook-form does not support `undefined` for controlled fields
            network_uuid: networkUuid ?? null,
            // NB: Must set default values for all required form fields
            targets: [],
            rules: [{ source_port: 80, target_port: 80 }],
            ...inputs,
        },
    });
    const { watch, control, setValue } = form;

    const onSubmit: SubmitHandler<BalancerCreateInputs> = async ({ location, ...body }) => {
        await createMutation.mutateAsync({
            location,
            body: { ...body, network_uuid: notNull(body.network_uuid, "network_uuid") },
        });
    };

    if (createMutation.isSuccess) {
        return <Navigate {...loadBalancerLink(createMutation.data)} />;
    }

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            {locations.showLocations && (
                <>
                    <LocationField
                        locations={locations}
                        afterChange={() => {
                            setValue("network_uuid", null);
                        }}
                    />
                    <Separator />
                </>
            )}

            <CreateAndAssignIpField<BalancerCreateInputs>
                name="reserve_public_ip"
                control={control}
                location={watch("location")}
            />

            {privateNetworksEnabled && (
                <FormField
                    isRequired
                    label={t`VPC network`}
                    description={t`Choose the VPC network the target servers are in.`}
                >
                    <Controller
                        control={control}
                        name="network_uuid"
                        render={({ field }) => (
                            <VpcNetworkSelect
                                location={watch("location")}
                                isDisabled={field.disabled}
                                defaultValueKey={field.value}
                                onChange={(vpc) => field.onChange(vpc?.uuid ?? null)}
                            />
                        )}
                    />
                </FormField>
            )}

            <Separator />

            <Controller
                control={control}
                name="targets"
                rules={{ validate: (value) => value.length > 0 || t`Add at least one target server` }}
                render={({ field, fieldState: { error } }) => (
                    <FormField wide isRequired label={t`Target servers`} errorMessage={error?.message}>
                        <TargetServersList
                            location={watch("location")}
                            networkUuid={watch("network_uuid")}
                            defaultTargets={field.value}
                            onInputChange={field.onChange}
                        />
                    </FormField>
                )}
            />

            <Separator />

            <Controller
                control={control}
                name="rules"
                rules={{ validate: (value) => value.length > 0 || t`Add at least one forwarding rule` }}
                render={({ fieldState }) => {
                    const { error } = fieldState;
                    // XXX: For some reason errors become arrays here
                    const realError = isArray(error) ? error.root : error;
                    return (
                        <FormField wide isRequired label={t`Forwarding rules`} errorMessage={realError?.message}>
                            <ForwardingRulesList />
                        </FormField>
                    );
                }}
            />

            <Separator />

            <DisplayNameField isRequired />

            <Suspense>
                <BalancerMonthlyCostField
                    location={watch("location")}
                    reserve_public_ip={watch("reserve_public_ip")}
                    billing_account_id={watch("billing_account_id")}
                />
            </Suspense>

            <BillingAccountField resourceType="load_balancer" isRequired />

            <Separator />

            <CreateFormAction resourceType="load_balancer" />
        </WHookForm>
    );
}

export function BalancerMonthlyCostField({
    location,
    reserve_public_ip,
    billing_account_id,
}: {
    location: string;
    reserve_public_ip: boolean;
    billing_account_id?: number;
}) {
    const prices = useSuspenseQueryAtom(pricesAtom);

    const price = getLoadBalancerPrice(prices, location, reserve_public_ip);
    return <MonthlyCostField price={price} defaultLocation={location} billingAccountId={billing_account_id} />;
}

interface VpcCreateInputs extends CommonInputs {
    subnet?: string;
}

export function VpcCreateForm({ inputs }: { inputs?: Partial<VpcCreateInputs> }) {
    //#region Hooks
    const { t } = useLingui();
    const vpcMutation = useStandardMutation(createVpcNetworkMutation);
    const FF_customSubnetEnabled = useAtomValue(FF_customSubnetEnabledAtom);

    const locations = useLocationsForType("vpc");
    const [value, setValue] = useState<"automatic" | "custom">("automatic");

    const form = useForm<VpcCreateInputs>({
        disabled: vpcMutation.isPending,
        defaultValues: {
            // NB: Must set default values for all required form fields
            name: "",
            location: locations.defaultLocation,
            ...inputs,
        },
    });
    //#endregion

    const onSubmit: SubmitHandler<VpcCreateInputs> = async ({ location, ...body }) => {
        if (value === "automatic") {
            body = omit(body, ["subnet"]);
        }
        await vpcMutation.mutateAsync({ location, body });
    };

    if (vpcMutation.isSuccess) {
        return <Navigate {...vpcLink(vpcMutation.data)} />;
    }

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            {locations.showLocations && (
                <>
                    <LocationField locations={locations} />
                    <Separator />
                </>
            )}

            {FF_customSubnetEnabled && (
                <FormField isRequired label={t`IP range`}>
                    <RadioGroup
                        className={R.RadioGroup}
                        aria-label="Connection details type"
                        value={value}
                        orientation="horizontal"
                        name="ip_range"
                        onChange={(v) => setValue(v as typeof value)}
                    >
                        <Radio className={RA.Radio} value="automatic">
                            <Trans comment="Explanation: Generate the subnet range automatically">
                                Generate (recommended)
                            </Trans>
                        </Radio>
                        <Radio className={RA.Radio} value="custom">
                            <Trans comment="Alternative: Configure a custom subnet range">Configure my own</Trans>
                        </Radio>
                    </RadioGroup>
                </FormField>
            )}

            {FF_customSubnetEnabled && value === "custom" && <AddressRangeField isRequired />}

            <Separator />

            <NameField isRequired />

            <Separator />

            <MonthlyCostField description={t`VPC network is available at no additional cost.`}>
                <MonthlyCostElement price={FREE_PRICE} />
            </MonthlyCostField>

            <Separator />

            <CreateFormAction resourceType="vpc" />
        </WHookForm>
    );
}

export interface FirewallCreateInputs extends FirewallCreateBody, Pick<CommonInputs, "location"> {}

export interface FirewallFormRule extends FirewallRule {
    type: RuleType;
}

export interface FirewallForm
    extends Pick<FirewallCreateBody, "billing_account_id" | "display_name">, Pick<CommonInputs, "location"> {
    inbound_rules: FirewallFormRule[];
    outbound_rules: FirewallFormRule[];
    resources_assigned: FirewallAssignedResource[];
}

function FirewallCreateForm({ inputs }: { inputs?: Partial<FirewallCreateInputs> }) {
    const { t } = useLingui();
    const createMutation = useStandardMutation(createFirewallMutation);
    const addMutation = useStandardMutation(addResourceToFirewallMutation);
    const locations = useLocationsForType("firewall");

    const typeAny = notNull(ruleTypes.find((item) => item.type === "any"));

    const defaultRules: FirewallFormRule[] = [
        {
            type: typeAny,
            direction: "outbound",
            protocol: "any",
            port_start: 0,
            endpoint_spec_type: "any" as FirewallRuleEndpointSpecType,
            endpoint_spec: [],
        },
    ];

    const form = useForm<FirewallForm>({
        disabled: createMutation.isPending,
        defaultValues: {
            location: locations.defaultLocation,
            inbound_rules: [],
            outbound_rules: [...defaultRules],
            resources_assigned: [],
            ...inputs,
        },
    });

    const { watch, control } = form;

    const onSubmit: SubmitHandler<FirewallForm> = async ({
        location,
        inbound_rules,
        outbound_rules,
        resources_assigned,
        ...body
    }) => {
        const { uuid } = await createMutation.mutateAsync({
            location,
            body: {
                ...body,
                rules: [...inbound_rules, ...outbound_rules],
            },
        });

        if (resources_assigned?.length) {
            for (const item of resources_assigned) {
                await addMutation.mutateAsync({
                    location,
                    uuid,
                    vm_uuid: item.resource_uuid,
                });
            }
        }
    };

    if (createMutation.isSuccess) {
        return <Navigate {...firewallLink(createMutation.data)} />;
    }

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            {locations.showLocations && (
                <>
                    <LocationField locations={locations} />
                    <Separator />
                </>
            )}

            <Controller
                control={control}
                name="inbound_rules"
                render={({ fieldState }) => {
                    const { error } = fieldState;
                    // XXX: For some reason errors become arrays here
                    const realError = isArray(error) ? error.root : error;
                    return (
                        <FormField wide label={t`Inbound rules`} errorMessage={realError?.message}>
                            <p className="pb-4">{firewallInboundDescription}</p>
                            <RulesList direction="inbound" name="inbound_rules" />
                        </FormField>
                    );
                }}
            />

            <Separator />

            <Controller
                control={control}
                name="outbound_rules"
                render={({ fieldState }) => {
                    const { error } = fieldState;
                    // XXX: For some reason errors become arrays here
                    const realError = isArray(error) ? error.root : error;
                    return (
                        <FormField wide label={t`Outbound rules`} errorMessage={realError?.message}>
                            <p className="pb-4">{firewallOutboundDescription}</p>
                            <RulesList direction="outbound" name="outbound_rules" />
                        </FormField>
                    );
                }}
            />

            <Separator />

            <Controller
                control={control}
                name="resources_assigned"
                render={({ field }) => (
                    <FormField wide label={t`Assigned to`}>
                        <AssignedToList
                            location={watch("location")}
                            value={field.value}
                            onInputChange={field.onChange}
                        />
                    </FormField>
                )}
            />

            <Separator />

            <DisplayNameField isRequired />

            <MonthlyCostField description={t`Firewall is available at no additional cost.`}>
                <MonthlyCostElement price={FREE_PRICE} />
            </MonthlyCostField>

            <BillingAccountField resourceType="firewall" isRequired />

            <Separator />

            <CreateFormAction resourceType="firewall" />
        </WHookForm>
    );
}

function NameField({ isRequired }: { isRequired: boolean }) {
    const { t } = useLingui();
    const form = useFormContext<CommonInputs>();
    const { ref, props } = useAriaField("name", form, { isRequired });
    return (
        <WTextField {...props} label={t`Resource name`}>
            <Input ref={ref} className={cn(FF.FormFieldInput, TF.Input)} />
        </WTextField>
    );
}

function DisplayNameField({ isRequired }: { isRequired: boolean }) {
    const { t } = useLingui();
    const form = useFormContext<BalancerCreateInputs>();
    const { ref, props } = useAriaField("display_name", form, { isRequired });
    return (
        <WTextField {...props} label={t`Resource name`}>
            <Input ref={ref} className={cn(FF.FormFieldInput, TF.Input)} />
        </WTextField>
    );
}

function AddressRangeField({ isRequired }: { isRequired?: boolean }) {
    const { t } = useLingui();
    const form = useFormContext<VpcCreateInputs>();
    const { ref, props } = useAriaField("subnet", form, { isRequired });

    const description = (
        <div>
            <Trans>The subnet must fall within one the following IP blocks:</Trans>
            <ul className="text-muted m-0 p-0 pl-4">
                <li>
                    <code>10.0.*.*/16</code> – <code>10.255.*.*/16</code>
                </li>
                <li>
                    <code>172.16.*.*/16</code> – <code>172.31.*.*/16</code>
                </li>
                <li>
                    <code>192.168.*.*/16</code>
                </li>
                <li>
                    <code>100.64.*.*/16</code> – <code>100.127.*.*/16</code>
                </li>
            </ul>
        </div>
    );

    return (
        <WTextField className="-mt-2" {...props} label={t`Subnet range (CIDR)`} description={description}>
            <Input ref={ref} placeholder="10.1.2.0/24" className={cn(FF.FormFieldInput, TF.Input)} />
        </WTextField>
    );
}

export function NetworkCreate(props: NetworkCreateProps) {
    return (
        <>
            <CreateHero />
            <NetworkForm {...props} />
        </>
    );
}

function CreateHero() {
    const { t } = useLingui();
    return (
        <HeroBlock title={t`Create new`} icon="jp-network-icon">
            <Trans>Start managing your networks by creating a new network resource.</Trans>
        </HeroBlock>
    );
}
